import type { ErrorCode, ExpiredLink } from './contract';

export interface ApiErrorBody {
  /**
   * An `ErrorCode` from the contract (a code it doesn't list yet passes through as is), or
   * `http_<status>` when the body wasn't the envelope.
   */
  code: ErrorCode | `http_${number}` | (string & {});
  message: string;
  fields?: Record<string, string[]>;
  /** Only on `410 link_expired`: what S14-expired shows. */
  details?: ExpiredLink;
  /** `429`: the `Retry-After` header, in seconds. */
  retryAfter?: number | null;
}

/** A failed call, normalised: HTTP status + the contract's error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorBody['code'];
  readonly fields: Record<string, string[]>;
  /** `410 link_expired` only (the one extension of the envelope). */
  readonly expiredLink: ExpiredLink | null;
  /** `429 rate_limited`: seconds until trying again makes sense (`Retry-After`), if the API said. */
  readonly retryAfter: number | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fields = body.fields ?? {};
    this.expiredLink = body.details ?? null;
    this.retryAfter = body.retryAfter ?? null;
  }

  get isValidation() {
    return this.status === 422;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
  /** No session (401) or no grant for this enquiry (403): the id alone opens nothing. */
  get isNoAccess() {
    return this.status === 401 || this.status === 403;
  }
  /** An expired or revoked magic link → S14-expired (`expiredLink` has the details). */
  get isExpired() {
    return this.status === 410;
  }
  get isRateLimited() {
    return this.status === 429;
  }
  /** The first message for a request field (`validation_failed`), e.g. `fieldError('email')`. */
  fieldError(field: string): string | undefined {
    return this.fields[field]?.[0];
  }
}

/**
 * The network itself failed (offline, DNS, CORS), or the answer took longer than the call's
 * budget (`timedOut`). Distinct from ApiError. The caller's own aborts are rethrown as-is.
 */
export class NetworkError extends Error {
  readonly timedOut: boolean;
  constructor(cause: unknown, { timedOut = false }: { timedOut?: boolean } = {}) {
    super(timedOut ? 'Request timed out' : 'Network request failed', { cause });
    this.name = 'NetworkError';
    this.timedOut = timedOut;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
/** `429 rate_limited`: the API wants a pause; "try again" at once would fail the same way. */
export const isRateLimited = (e: unknown): boolean => e instanceof ApiError && e.isRateLimited;

/**
 * The Turnstile check of a guarded call failed (`turnstile_required` / `turnstile_failed`,
 * w5-11). The HTTP client already tried once more with a fresh token; the screen says so.
 */
export const isTurnstileError = (e: unknown): boolean =>
  e instanceof ApiError && (e.code === 'turnstile_required' || e.code === 'turnstile_failed');
